import { Box, xcss } from '@atlaskit/primitives';
import {
  AsyncSelect,
  type FormatOptionLabelMeta,
  type SelectProps
} from '@databases/atlaskit/Select';
import { authenticateJira } from '@databases/connections/jira-auth';
import JiraIssueIcon from '@databases/content-link/JiraIssueIcon';
import JiraIssueLabel from '@databases/content-link/JiraIssueLabel';
import JiraIssueLink from '@databases/content-link/JiraIssueLink';
import JiraIssueStatus from '@databases/content-link/JiraIssueStatus';
import { messages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import { currentSnackbarState } from '@databases/snackbar/snackbar';
import {
  type CloudId,
  type JiraIssueRef,
  type JiraServerId
} from '@databases/types/confluence';
import { DatabaseFieldType } from '@databases/types/database';
import { JiraAuthenticationError } from '@databases/utils/confluence/jira';
import {
  type ValueChange,
  type MultiChange,
  MultiValueChangeType
} from '@databases/utils/database/changesets';
import { searchJiraIssues } from '@databases/utils/redux/jira/actions';
import { generateId } from '@databases/utils/utils';
import React, { type FunctionComponent, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl-next';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';

import {
  CustomValueContainer,
  baseSelectStyleConfig,
  compactOptionSelectStyleConfig,
  isRenderedInSidePanelStyling
} from '../popover-edit-common';

import { type ResolvedValue, type Value } from './types';

type InputActionMeta = { action?: string };

const getOptionValue = (value: JiraIssueRef) => value.key;

const getOptionLabel = (value: JiraIssueRef) => value.key;

const isJiraIssueSelectOption = (
  value: ResolvedValue
): value is ResolvedValue & JiraIssueRef => 'key' in value;

const optionContainerStyles = xcss({
  display: 'flex',
  gap: 'space.050',
  alignItems: 'center'
});

export interface JiraIssueSelectProps {
  cloudId: CloudId;
  serverId: JiraServerId;
  onChange: (value: Value[], change: ValueChange | null) => void;
  onFocus?: (e: React.FocusEvent<HTMLElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLElement>) => void;
  autoFocus?: boolean;
  resolvedValues: ResolvedValue[];
  isRenderedInSidePanel?: boolean;
  isRenderedInContextualFieldMenu?: boolean;
  isMulti: boolean;
  addDataSectionRef: React.MutableRefObject<HTMLDivElement | null>;
  initialKey?: string;
}

const JiraIssueSelect: FunctionComponent<JiraIssueSelectProps> = ({
  cloudId,
  serverId,
  onChange,
  onFocus,
  onBlur,
  autoFocus,
  resolvedValues,
  isRenderedInSidePanel,
  isRenderedInContextualFieldMenu,
  isMulti,
  addDataSectionRef,
  initialKey
}) => {
  const [inputValue, setInputValue] = useState(initialKey ?? '');
  const handleInputChange = useCallback((val: string, meta?: InputActionMeta) => {
    if (meta && meta.action !== 'input-change') {
      return;
    }
    setInputValue(val);
  }, []);
  const { formatMessage } = useIntl();
  const dispatch = defaultStoreUtils.useDispatch();
  const store = defaultStoreUtils.useStore();
  const selectValue = useMemo(
    () => resolvedValues.filter(isJiraIssueSelectOption),
    [resolvedValues]
  );

  const addJiraItemToCollab = useCallback(
    (val: Value) => {
      const jiraValues = isMulti ? [...resolvedValues, val] : [val];

      const delta: MultiChange<DatabaseFieldType.JIRA_ISSUE> = isMulti
        ? {
            type: MultiValueChangeType.APPEND,
            value: val
          }
        : {
            type: MultiValueChangeType.SET_TO,
            value: [val]
          };

      onChange(jiraValues, {
        type: DatabaseFieldType.JIRA_ISSUE,
        delta
      });
    },
    [onChange, resolvedValues, isMulti]
  );

  const handleChange: SelectProps<JiraIssueRef, boolean>['onChange'] = useCallback(
    (val: JiraIssueRef | readonly JiraIssueRef[] | null) => {
      if (Array.isArray(val)) {
        setInputValue('');
        const jiraValues = val.map((issue) => ({
          _id:
            typeof (issue as { _id?: unknown })._id === 'string'
              ? (issue as unknown as { _id: string })._id
              : generateId(),
          key: issue.key
        }));

        onChange(jiraValues, {
          type: DatabaseFieldType.JIRA_ISSUE,
          delta: {
            type: MultiValueChangeType.SET_TO,
            value: jiraValues
          }
        });
        return;
      }
      if (!val) {
        setInputValue('');
        onChange([], {
          type: DatabaseFieldType.JIRA_ISSUE,
          delta: {
            type: MultiValueChangeType.SET_TO,
            value: []
          }
        });
        return;
      }
      const valToAdd = {
        _id: generateId(),
        key: 'key' in val ? val.key : ''
      };
      setInputValue('');
      addJiraItemToCollab(valToAdd);
    },
    [addJiraItemToCollab, onChange]
  );

  const loadOptions = useCallback(
    async (inputValue: string) => {
      try {
        if (inputValue.length > 1) {
          return await dispatch(searchJiraIssues(cloudId, serverId, inputValue));
        }
        return [];
        // eslint-disable-next-line canvas/no-swallow-errors-in-catch
      } catch (e) {
        if (e instanceof JiraAuthenticationError && e.authenticationPath) {
          const authPath = e.authenticationPath;

          let flagShown = false;

          if (flagShown) {
            return [];
          }

          currentSnackbarState.setMessage(
            formatMessage(messages['snackbar-error.jira.auth-required']),
            {
              id: 'jira-auth',
              type: 'error',
              showCloseButton: true,
              actions: [
                {
                  content: formatMessage(messages['snackbar-error.jira.authenticate']),
                  onClick: async () => {
                    await authenticateJira(
                      cloudId,
                      serverId,
                      authPath,
                      store,
                      formatMessage(messages['snackbar-error.enable-popup'])
                    );
                    flagShown = false;
                  }
                }
              ],
              onDismiss: () => {
                flagShown = false;
              }
            }
          );
          flagShown = true;

          return [];
        } else {
          throw e;
        }
      }
    },
    [cloudId, dispatch, formatMessage, serverId, store]
  );

  const formatOptionLabel = useCallback(
    (value: JiraIssueRef, meta: FormatOptionLabelMeta<JiraIssueRef>) => {
      const issue = {
        ...value,
        cloudId,
        serverId
      };

      if (meta.context === 'value') {
        return <JiraIssueLink issue={issue} useSmartLink={false} />;
      }

      return (
        <Box xcss={optionContainerStyles} testId="jira-issue-option-container">
          <JiraIssueIcon issue={issue} />
          <JiraIssueLabel issue={issue} />
          <JiraIssueStatus issue={issue} />
        </Box>
      );
    },
    [cloudId, serverId]
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLElement>) => {
      if (onBlur) {
        onBlur(e);
      }
      if (!isRenderedInSidePanel && !isRenderedInContextualFieldMenu) {
        addDataSectionRef.current?.getElementsByTagName('input')[0].focus();
      }
    },
    [onBlur, addDataSectionRef, isRenderedInContextualFieldMenu, isRenderedInSidePanel]
  );

  return (
    <AsyncSelect<JiraIssueRef, boolean>
      onChange={handleChange}
      getOptionLabel={getOptionLabel}
      loadOptions={loadOptions}
      getOptionValue={getOptionValue}
      onFocus={onFocus}
      onBlur={handleBlur}
      blurInputOnSelect={false}
      closeMenuOnSelect={false}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      formatOptionLabel={formatOptionLabel}
      placeholder={formatMessage(
        isFeatureFlagEnabled('canvas-issue-terminology-refresh-update')
          ? messages['jira-issue-picker-popover.search-text-issue-terminology-refresh-m4']
          : messages['jira-issue-picker-popover.search-text']
      )}
      loadingMessage={() =>
        formatMessage(messages['field-settings.select-picker-loading-text'])
      }
      noOptionsMessage={() => formatMessage(messages['generic-select.no-options'])}
      autoFocus={autoFocus}
      styles={
        isRenderedInContextualFieldMenu
          ? compactOptionSelectStyleConfig
          : isRenderedInSidePanel
            ? isRenderedInSidePanelStyling
            : baseSelectStyleConfig
      }
      spacing={isRenderedInContextualFieldMenu ? 'compact' : undefined}
      controlShouldRenderValue={Boolean(isRenderedInContextualFieldMenu)}
      value={isRenderedInContextualFieldMenu ? selectValue : null}
      {...(isRenderedInContextualFieldMenu && {
        isClearable: resolvedValues.length > 0,
        isMulti
      })}
      {...(!isRenderedInSidePanel &&
        !isRenderedInContextualFieldMenu && {
          menuPortalTarget: null,
          defaultMenuIsOpen: true,
          menuIsOpen: true,
          components: {
            IndicatorsContainer: () => null,
            ValueContainer: CustomValueContainer
          }
        })}
    />
  );
};

export default JiraIssueSelect;
